export type Paise = number;
export type BaseQuantity = string;

/**
 * Canonical aggregate boundaries shared by API commands and all clients.
 * Legacy tables may remain as read-only lineage during migration, but they
 * are not alternative authorities for these facts.
 */
export type CanonicalAggregate =
  | "SALE"
  | "INVENTORY"
  | "PROCUREMENT"
  | "PAYMENT"
  | "TAX_DOCUMENT"
  | "PARTY_BALANCE";

export const CANONICAL_DOMAIN_CONTRACT = {
  SALE: {
    authority: "revival_sales_and_sale_quotes",
    immutableFacts: [
      "sale_lines",
      "sale_lot_allocations",
      "sale_workflow_events",
    ],
  },
  INVENTORY: {
    authority: "stock_movements_and_stock_balances",
    immutableFacts: [
      "inventory_lots",
      "stock_movements",
      "stock_balances",
      "inventory_location_movements",
      "inventory_location_balances",
    ],
  },
  PROCUREMENT: {
    authority: "canonical_procurement_documents",
    immutableFacts: [
      "canonical_procurement_lines",
      "procurement_document_events",
      "goods_receipts",
      "goods_receipt_items",
      "purchase_orders",
      "purchase_invoices",
      "supplier_payment_allocations",
      "procurement_tax_controls",
    ],
  },
  PAYMENT: {
    authority: "payment_attempts_and_canonical_payment_allocations",
    immutableFacts: [
      "payment_operation_events",
      "canonical_payment_allocations",
      "counter_cash_movements",
      "customer_value_ledger",
    ],
  },
  TAX_DOCUMENT: {
    authority: "tax_facts_and_statutory_documents",
    immutableFacts: [
      "statutory_document_lines",
      "statutory_document_events",
      "tax_facts",
      "gst_provider_attempts",
    ],
  },
  PARTY_BALANCE: {
    authority: "party_subledger_entries",
    immutableFacts: [
      "customer_ledger_entries",
      "supplier_ledger_entries",
      "advance_allocations",
    ],
  },
} as const satisfies Record<
  CanonicalAggregate,
  { authority: string; immutableFacts: readonly string[] }
>;

export interface CanonicalCommandContext {
  tenantId: string;
  storeId: string;
  actorId: string;
  idempotencyKey: string;
  requestHash: string;
  correlationId?: string;
  commandType: string;
}

export function assertCanonicalCommandContext(
  value: Partial<CanonicalCommandContext>,
): asserts value is CanonicalCommandContext {
  const required: Array<keyof CanonicalCommandContext> = [
    "tenantId",
    "storeId",
    "actorId",
    "idempotencyKey",
    "requestHash",
    "commandType",
  ];
  const missing = required.filter(
    (field) => typeof value[field] !== "string" || !value[field]!.trim(),
  );
  if (missing.length) {
    throw new Error(`CANONICAL_COMMAND_CONTEXT_INVALID:${missing.join(",")}`);
  }
}

export type StableCommandErrorCode =
  | "ACTIVE_STORE_REQUIRED"
  | "STORE_ACCESS_DENIED"
  | "STORE_CONTEXT_STALE"
  | "CROSS_SCOPE_DENIED"
  | "COUNTER_SCOPE_DENIED"
  | "IDEMPOTENCY_KEY_CONFLICT"
  | "IDEMPOTENCY_ENVELOPE_CONFLICT"
  | "IDEMPOTENCY_IN_PROGRESS"
  | "CLIENT_UPGRADE_REQUIRED"
  | "QUOTE_STALE"
  | "INSUFFICIENT_STOCK"
  | "TENDER_TOTAL_MISMATCH"
  | "COMPLIANCE_DATA_REQUIRED"
  | "CAPABILITY_DISABLED"
  | "REVIVAL_RELEASE_FREEZE"
  | "OFFLINE_CURSOR_INVALID"
  | "OFFLINE_CURSOR_SCOPE_MISMATCH"
  | "OFFLINE_CURSOR_SECRET_UNCONFIGURED"
  | "OFFLINE_SNAPSHOT_INCOMPLETE"
  | "OFFLINE_SALE_PATH_UNSUPPORTED"
  | "OFFLINE_COMPLIANCE_PATH_UNSUPPORTED"
  | "OFFLINE_QUOTE_SNAPSHOT_INVALID"
  | "OFFLINE_SALE_REQUIRES_REVIEW";

export interface CommandContext {
  tenantId: string;
  storeId: string;
  actorId: string;
  idempotencyKey: string;
  requestHash: string;
  clientVersion: string;
  correlationId: string;
  commandType: string;
}

export interface CommandError {
  code: StableCommandErrorCode;
  message: string;
  correlationId: string;
  retryable: boolean;
  action: StableErrorAction;
  details?: Readonly<Record<string, string | number | boolean>>;
}

export type StableErrorAction =
  | "CORRECT_REQUEST"
  | "REAUTHENTICATE"
  | "REQUEST_ACCESS"
  | "SELECT_AUTHORIZED_SCOPE"
  | "REFRESH_RESOURCE"
  | "REQUOTE"
  | "REDUCE_QUANTITY_OR_REQUOTE"
  | "RETRY_SAME_REQUEST"
  | "RETRY_LATER"
  | "USE_NEW_IDEMPOTENCY_KEY"
  | "UPGRADE_CLIENT"
  | "CONTACT_SUPPORT";

/** Public error envelope returned by every API surface and understood by clients. */
export interface StableApiErrorResponse {
  statusCode: number;
  timestamp: string;
  path: string;
  code: string;
  message: string;
  correlationId: string;
  retryable: boolean;
  action: StableErrorAction;
  details?: Readonly<Record<string, unknown>>;
  errors?: readonly unknown[];
}

const STABLE_ERROR_ACTIONS = new Set<StableErrorAction>([
  "CORRECT_REQUEST",
  "REAUTHENTICATE",
  "REQUEST_ACCESS",
  "SELECT_AUTHORIZED_SCOPE",
  "REFRESH_RESOURCE",
  "REQUOTE",
  "REDUCE_QUANTITY_OR_REQUOTE",
  "RETRY_SAME_REQUEST",
  "RETRY_LATER",
  "USE_NEW_IDEMPOTENCY_KEY",
  "UPGRADE_CLIENT",
  "CONTACT_SUPPORT",
]);

export function parseStableApiError(
  value: unknown,
): StableApiErrorResponse | undefined {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return undefined;
  const body = value as Record<string, unknown>;
  if (
    typeof body.statusCode !== "number" ||
    typeof body.timestamp !== "string" ||
    typeof body.path !== "string" ||
    typeof body.code !== "string" ||
    typeof body.message !== "string" ||
    typeof body.correlationId !== "string" ||
    typeof body.retryable !== "boolean" ||
    typeof body.action !== "string" ||
    !STABLE_ERROR_ACTIONS.has(body.action as StableErrorAction)
  ) {
    return undefined;
  }
  return body as unknown as StableApiErrorResponse;
}

export class StableApiError extends Error {
  readonly name = "StableApiError";
  readonly response: StableApiErrorResponse;
  readonly statusCode: number;
  readonly code: string;
  readonly correlationId: string;
  readonly retryable: boolean;
  readonly action: StableErrorAction;

  constructor(response: StableApiErrorResponse) {
    super(response.message);
    this.response = response;
    this.statusCode = response.statusCode;
    this.code = response.code;
    this.correlationId = response.correlationId;
    this.retryable = response.retryable;
    this.action = response.action;
  }
}

export type TenderType = "CASH" | "UPI" | "CARD" | "LOYALTY";

export interface SaleQuoteLotAllocation {
  lotId: string;
  batchNumber: string;
  expiry: string | null;
  quantity: BaseQuantity;
  balanceVersion: number;
}

export interface SaleQuoteLine {
  lineId: string;
  productId: string;
  storeProductVersion: number;
  quantity: BaseQuantity;
  freeQuantity: BaseQuantity;
  unitMrpPaise: Paise;
  unitPricePaise: Paise;
  gstBps: number;
  discountBps: number;
  taxablePaise: Paise;
  taxPaise: Paise;
  totalPaise: Paise;
  complianceFields: readonly string[];
  warnings?: readonly string[];
  proposedLots: readonly SaleQuoteLotAllocation[];
}

export interface SaleQuote {
  quoteId: string;
  version: number;
  tenantId: string;
  storeId: string;
  createdAt: string;
  expiresAt: string;
  customerId: string | null;
  lines: readonly SaleQuoteLine[];
  subtotalPaise: Paise;
  discountPaise: Paise;
  taxablePaise: Paise;
  taxPaise: Paise;
  roundOffPaise: Paise;
  payablePaise: Paise;
  allowedTenders: readonly TenderType[];
}

export interface TenderAllocation {
  type: TenderType;
  amountPaise: Paise;
  reference?: string;
  paymentAttemptId?: string;
}

export interface AcceptSaleCommand {
  quoteId: string;
  quoteVersion: number;
  counterId: string;
  billingSessionId: string;
  tenders: readonly TenderAllocation[];
  compliance?: Readonly<Record<string, string>>;
  print: boolean;
}

export interface AcceptedSale {
  saleId: string;
  invoiceNumber: string;
  quoteId: string;
  payablePaise: Paise;
  acceptedAt: string;
  replayed: boolean;
}
